// Pure, dependency-free logic for the Day 30 Progress Review + explicit Final Completion of a
// Findability Cycle. API functions are injected so this stays trivially unit-testable (mirrors
// baselineFlow.js). It NEVER invents a second review model — it uses the canonical backend keys.

// Canonical Progress Review self-assessment questions. SAME required keys as the backend
// (REQUIRED_REVIEW_ANSWER_KEYS = q1..q4). Do NOT invent new keys.
export const PROGRESS_REVIEW_QUESTIONS = [
  { key: "q1", text: "Did it name you or your work specifically?" },
  { key: "q2", text: "Did it use the specific words or approach only you use — your method, your process, your language?" },
  { key: "q3", text: "Or did it default to generic advice for the broader category?" },
  { key: "q4", text: "Did it name other specific people, brands, or tools as the go-to answer?" },
];

export const REQUIRED_REVIEW_ANSWER_KEYS = ["q1", "q2", "q3", "q4"];

export function missingReviewFields({ note, answers } = {}) {
  const missing = [];
  if (!(note || "").trim()) missing.push("note");
  const a = answers || {};
  REQUIRED_REVIEW_ANSWER_KEYS.forEach((k) => {
    if (!String(a[k] || "").trim()) missing.push(k);
  });
  return missing;
}

export const isReviewComplete = (draft) => missingReviewFields(draft).length === 0;

// Gate: show the Progress Review entry surface ONLY for a cycle-mode, active cycle with all 30
// efforts complete whose review is not yet confirmed.
export function shouldShowProgressReview({ cycleMode, cycle, completedCount } = {}) {
  const c = cycle || {};
  return !!cycleMode
    && Number(completedCount) === 30
    && c.status === "active"
    && c.progress_review_confirmed === false;
}

// Gate: the explicit Final Completion action is available only once the cycle is ready AND the
// server has issued a completion-action token.
export function canCompleteCycle(cycle) {
  const c = cycle || {};
  return c.ready_to_complete === true && !!c.completion_action_token;
}

// Exact backend payload: note + q1..q4 answers only.
export function buildReviewPayload({ note, answers } = {}) {
  return {
    note: (note || "").trim(),
    answers: REQUIRED_REVIEW_ANSWER_KEYS.reduce((acc, k) => {
      acc[k] = (answers && answers[k]) || "";
      return acc;
    }, {}),
  };
}

const MESSAGES = {
  no_active_cycle: "We couldn't find an active cycle for a Progress Review. Please refresh and try again.",
  baseline_not_confirmed: "Confirm your baseline first.",
  cycle_not_active: "The Progress Review can't be changed in the cycle's current state.",
  cycle_terminal: "This cycle is closed.",
  progress_review_efforts_incomplete: "Complete all 30 efforts before your Progress Review.",
  progress_review_incomplete: "Add your findings and answer all four questions.",
  progress_review_missing: "Save your Progress Review before confirming.",
  progress_review_not_editable: "The Progress Review can't be edited right now.",
  progress_review_already_confirmed: "Your Progress Review is already confirmed.",
  cycle_not_ready: "Your cycle isn't ready to complete yet.",
  progress_review_not_canonical: "Confirm your Progress Review before completing.",
  completion_token_invalid: "This completion link is invalid or expired. Please refresh and try again.",
  completion_token_stale: "This completion link is no longer valid. Please refresh and try again.",
};

export function friendlyReviewError(err) {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  const code = detail && detail.code;
  return MESSAGES[code] || "Something went wrong. Please try again.";
}

// Save Draft: PUT the draft ONLY. Never confirms, never advances the cycle.
export async function runSaveReview({ payload, saveProgressReview }) {
  try {
    const result = await saveProgressReview(payload);
    return { ok: true, result };
  } catch (e) {
    return { ok: false, message: friendlyReviewError(e) };
  }
}

// Confirm: canonical POST confirm ONLY, then re-fetch the authoritative lifecycle so the caller
// renders the next state (ready_to_complete) from the server — never forced by the client.
export async function runConfirmReview({ confirmProgressReview, getLifecycle }) {
  try {
    await confirmProgressReview();
    const lifecycle = await getLifecycle();
    return { ok: true, lifecycle };
  } catch (e) {
    return { ok: false, message: friendlyReviewError(e) };
  }
}

// Complete: the ONE explicit final action. POST /cycle/complete with the canonical token, then
// re-fetch the lifecycle so the caller renders the completed (terminal) state from the server.
export async function runCompleteCycle({ completeCycle, getLifecycle, token }) {
  try {
    const result = await completeCycle(token);
    const lifecycle = await getLifecycle();
    return { ok: true, result, lifecycle };
  } catch (e) {
    return { ok: false, message: friendlyReviewError(e) };
  }
}
